import React, { useMemo } from 'lib/react';
import {
  EVGrid, EVPagination, makeStyles, EVSkeleton,
} from 'lib/components';
import { useSelector, useDispatch } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import SelectField from 'commonComponents/select-field';
import { scrollToTop } from 'utils/app.utils';
import { PAGE_SIZE_OPTIONS } from 'features/orderHistory/OrderHistory.constants';
import { UPDATE_REPORT_ANALYTICS_PAGE_OPTIONS } from 'constants/ActionTypes';

const useStyles = makeStyles((theme) => ({
  paginationContainer: {
    padding: '24px',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 640px)': { flexDirection: 'row' },
  },
  pageSizeContainer: {
    width: 'fit-content',
    columnGap: '10px',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '19px',
    color: theme.evPalette.charcoal[900],
  },
  pageSizeSelect: {
    minWidth: '71px',
    height: '32px',
    '& fieldset': { borderColor: theme.evPalette.charcoal[600] },
  },
}));

/** Component to render the Page options for Order History Page */
function PageOptions({ loading }) {
  const { pageNumber, pageSize, reportsCount } = useSelector(({ analyticsDashboard }) => analyticsDashboard);

  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  const pageCount = useMemo(() => (loading ? 0 : Math.ceil(reportsCount / pageSize)), [pageSize, reportsCount, loading]);

  const handlePageSizeChange = ({ target: { value } }) => {
    dispatch({ type: UPDATE_REPORT_ANALYTICS_PAGE_OPTIONS, pageOptions: { pageSize: value, pageNumber: 1 } });
    scrollToTop();
  };

  const handlePageNumberChange = (_, page) => {
    dispatch({ type: UPDATE_REPORT_ANALYTICS_PAGE_OPTIONS, pageOptions: { pageNumber: page } });
    scrollToTop();
  };

  return (
    <EVGrid container justifyContent="space-between" alignItems="center" className={classes.paginationContainer}>
      <EVGrid container wrap="nowrap" alignItems="center" className={classes.pageSizeContainer}>
        {loading ? <EVSkeleton style={{ borderRadius: '6px' }} width={192} height={32} /> : (
          <>
            <span aria-hidden>{t('order_history__show')}</span>
            <SelectField
              selectId="pageSize"
              selectedValue={pageSize}
              selectOptions={PAGE_SIZE_OPTIONS}
              handleChange={handlePageSizeChange}
              variant="outlined"
              IconComponent={undefined}
              className={classes.pageSizeSelect}
              aria-label={`Show ${pageSize} records`}
              aria-labelledby={undefined}
            />
            <span aria-hidden>{t('order_history__records')}</span>
          </>
        )}
      </EVGrid>
      {loading ? <EVSkeleton style={{ borderRadius: '6px' }} width={192} height={32} /> : (
        <EVPagination
          data-testid="pagination"
          count={pageCount}
          onChange={handlePageNumberChange}
          page={pageNumber}
          siblingCount={1}
          boundaryCount={1}
        />
      )}
    </EVGrid>
  );
}

PageOptions.propTypes = {
  loading: PropTypes.bool.isRequired,
};

export default PageOptions;
